/**
 * The searchable icon picker (design/reboot/icons.js `open()` ported to React; same markup and classes:
 * `.di-dialog`, `.di-grid`, `.di-option` …). A separate native dialog, so it can open above another
 * dialog (the View builder) without closing it.
 *
 * Keyboard: the search has focus; ArrowDown enters the grid; arrows move by cell / by row (the grid's
 * real column count); Home / End; Enter or Space choose; Escape closes only the picker.
 */

import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type KeyboardEvent, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon.js';
import { allIconsLoaded, iconLabel, iconsVersion, loadAllIcons, resolveIcon, searchIcons, subscribeIcons } from './registry.js';

const BATCH = 100;

export interface IconPickerProps {
  /** The current icon name. */
  value: string;
  onChoose(name: string): void;
  onClose(): void;
}

export function IconPicker({ value, onChoose, onClose }: IconPickerProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLUListElement>(null);
  const focusAfterMore = useRef<number | null>(null);
  const [query, setQuery] = useState('');
  const [visible, setVisible] = useState(BATCH);
  useSyncExternalStore(subscribeIcons, iconsVersion, iconsVersion);
  const loaded = allIconsLoaded();

  useEffect(() => {
    void loadAllIcons().catch(() => undefined);
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    searchRef.current?.focus();
  }, []);

  const results = useMemo(() => (loaded ? searchIcons(query) : []), [loaded, query]);
  const shown = results.slice(0, visible);
  const selected = resolveIcon(value) ?? value;

  useEffect(() => {
    if (focusAfterMore.current === null) return;
    const options = gridRef.current?.querySelectorAll<HTMLButtonElement>('[data-di-name]');
    options?.[focusAfterMore.current]?.focus();
    focusAfterMore.current = null;
  }, [visible]);

  const options = (): HTMLButtonElement[] => Array.from(gridRef.current?.querySelectorAll<HTMLButtonElement>('[data-di-name]') ?? []);

  const onKeyDown = (event: KeyboardEvent<HTMLDialogElement>): void => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    const target = event.target as HTMLElement;
    if (target === searchRef.current && event.key === 'ArrowDown') {
      event.preventDefault();
      options()[0]?.focus();
      return;
    }
    const button = target.closest<HTMLButtonElement>('[data-di-name]');
    if (!button || !gridRef.current) return;
    const all = options();
    const index = all.indexOf(button);
    const columns = getComputedStyle(gridRef.current).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
    const offsets: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -columns, ArrowDown: columns, Home: -index, End: all.length - 1 - index };
    const offset = offsets[event.key];
    if (offset !== undefined) {
      event.preventDefault();
      all[Math.max(0, Math.min(all.length - 1, index + offset))]?.focus();
    }
  };

  const onDialogClick = (event: MouseEvent<HTMLDialogElement>): void => {
    // Never let the click reach the reader's or a parent form's delegated actions.
    event.stopPropagation();
    if (event.target === dialogRef.current) {
      const b = dialogRef.current.getBoundingClientRect();
      if (event.clientX < b.left || event.clientX > b.right || event.clientY < b.top || event.clientY > b.bottom) onClose();
    }
  };

  return createPortal(
    <dialog
      ref={dialogRef}
      className="di-dialog"
      aria-labelledby="di-picker-title"
      aria-describedby="di-picker-count"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onKeyDown={onKeyDown}
      onClick={onDialogClick}
    >
      <header className="di-header">
        <h2 className="di-heading" id="di-picker-title">Choose an icon</h2>
        <button type="button" className="di-close" aria-label="Close icon picker" onClick={onClose}>
          <Icon name="x" />
        </button>
      </header>
      <label className="di-search-row">
        <Icon name="search" />
        <input
          ref={searchRef}
          type="search"
          data-di-search=""
          placeholder="Search icons by name or keyword"
          aria-label="Search icons by name or keyword"
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setVisible(BATCH);
            if (scrollRef.current) scrollRef.current.scrollTop = 0;
          }}
        />
      </label>
      <div className="di-library-meta">
        <span id="di-picker-count" data-di-count="" role="status" aria-live="polite">
          {loaded ? `${results.length.toLocaleString('en')} ${results.length === 1 ? 'icon' : 'icons'}` : 'Loading icons…'}
        </span>
        <span>Lucide · all icons</span>
      </div>
      <div className="di-scroll" ref={scrollRef}>
        <ul className="di-grid" data-di-grid="" aria-label="Icons" ref={gridRef}>
          {shown.map((name) => (
            <li key={name}>
              <button type="button" className="di-option" data-di-name={name} aria-label={iconLabel(name)} aria-pressed={name === selected} onClick={() => onChoose(name)}>
                <Icon name={name} />
                <span>{iconLabel(name)}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="di-none" data-di-none="" hidden={!loaded || results.length > 0}>
          <strong>No matching icons</strong>Try a different name or keyword.
        </div>
        <button
          type="button"
          className="di-more"
          data-di-more=""
          hidden={shown.length >= results.length}
          onClick={() => {
            focusAfterMore.current = visible;
            setVisible((v) => v + BATCH);
          }}
        >
          {`Show ${Math.min(BATCH, results.length - shown.length)} more`}
        </button>
      </div>
      <footer className="di-footer">
        <span>
          <Icon name={selected} />
          Current: {iconLabel(selected)}
        </span>
        <a className="di-credit" href="/lucide-LICENSE.txt" target="_blank" rel="noopener noreferrer">
          Open-source license
        </a>
      </footer>
    </dialog>,
    document.body,
  );
}
